import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import {
  CalendarDays, Clock, Plus, Trash2, ArrowRight, Bell, BellOff, FileText,
  Loader2, CheckCircle2, AlertCircle, Send
} from 'lucide-react';
import { api, formatDate } from '../lib/api';
import { toast } from 'sonner';

export default function Schedule() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [newSchedule, setNewSchedule] = useState({
    assessment_type: 'full',
    scheduled_date: '',
    scheduled_time: '09:00',
    reminder_24h: true,
    notes: ''
  });

  useEffect(() => { fetchSchedules(); }, []);

  const fetchSchedules = async () => {
    try {
      const res = await api.get('/schedule/assessments');
      setSchedules(res.data.schedules || []);
    } catch (e) {
      console.error('Error fetching schedules:', e);
    } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newSchedule.scheduled_date) {
      toast.error('Please select a date');
      return;
    }
    setSaving(true);
    try {
      const isoDate = `${newSchedule.scheduled_date}T${newSchedule.scheduled_time}:00Z`;
      await api.post('/schedule/assessment', {
        assessment_type: newSchedule.assessment_type,
        scheduled_date: isoDate,
        reminder_24h: newSchedule.reminder_24h,
        notes: newSchedule.notes || null
      });
      toast.success('Assessment scheduled');
      setShowAddForm(false);
      setNewSchedule({ assessment_type: 'full', scheduled_date: '', scheduled_time: '09:00', reminder_24h: true, notes: '' });
      fetchSchedules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to schedule');
    } finally { setSaving(false); }
  };

  const handleCancel = async (scheduleId) => {
    try {
      await api.delete(`/schedule/assessment/${scheduleId}`);
      toast.success('Schedule cancelled');
      fetchSchedules();
    } catch (e) {
      toast.error('Failed to cancel schedule');
    }
  };

  const handleSendReminder = async (scheduleId) => {
    setSendingReminder(scheduleId);
    try {
      await api.post(`/email/send-reminder/${scheduleId}`);
      toast.success('Reminder email sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    } finally { setSendingReminder(null); }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const isUpcoming = (dateStr) => {
    const d = new Date(dateStr);
    return d > new Date();
  };

  const isPast = (dateStr) => {
    const d = new Date(dateStr);
    return d < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground font-mono text-sm">Loading schedule...</p>
        </div>
      </div>
    );
  }

  const upcoming = schedules.filter(s => s.status === 'scheduled' && isUpcoming(s.scheduled_date));
  const past = schedules.filter(s => s.status === 'completed' || (s.status === 'scheduled' && isPast(s.scheduled_date)));

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="schedule-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Assessment Schedule</h1>
          <p className="text-muted-foreground">Plan and track your cognitive diagnostic sessions</p>
        </div>
        <Button onClick={() => setShowAddForm(true)} data-testid="schedule-new-btn">
          <Plus className="mr-2 h-4 w-4" />
          Schedule Assessment
        </Button>
      </div>

      {/* New Schedule Form */}
      {showAddForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-2 border-primary/30 bg-primary/5">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-primary" strokeWidth={1.5} />
                Schedule New Assessment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Assessment Type</Label>
                  <Select
                    value={newSchedule.assessment_type}
                    onValueChange={v => setNewSchedule(p => ({ ...p, assessment_type: v }))}
                  >
                    <SelectTrigger data-testid="schedule-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="express">Express Scan (15 questions)</SelectItem>
                      <SelectItem value="full">Full Diagnostic (100 questions)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Date</Label>
                  <Input
                    type="date"
                    min={getMinDate()}
                    value={newSchedule.scheduled_date}
                    onChange={e => setNewSchedule(p => ({ ...p, scheduled_date: e.target.value }))}
                    data-testid="schedule-date-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Time (UTC)</Label>
                  <Input
                    type="time"
                    value={newSchedule.scheduled_time}
                    onChange={e => setNewSchedule(p => ({ ...p, scheduled_time: e.target.value }))}
                    data-testid="schedule-time-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea
                  value={newSchedule.notes}
                  onChange={e => setNewSchedule(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any notes about this assessment..."
                  rows={2}
                  data-testid="schedule-notes-input"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newSchedule.reminder_24h}
                    onCheckedChange={v => setNewSchedule(p => ({ ...p, reminder_24h: v }))}
                    data-testid="schedule-reminder-switch"
                  />
                  <Label className="text-sm text-muted-foreground">Send email reminder 24h before</Label>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => setShowAddForm(false)}>Cancel</Button>
                  <Button onClick={handleCreate} disabled={saving} data-testid="schedule-save-btn">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarDays className="mr-2 h-4 w-4" />}
                    Schedule
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Upcoming Assessments */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" strokeWidth={1.5} />
              Upcoming
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcoming.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarDays className="h-10 w-10 mx-auto mb-3 opacity-40" strokeWidth={1} />
                <p className="text-sm">No upcoming assessments scheduled</p>
                <p className="text-xs mt-1">Click "Schedule Assessment" to plan your next diagnostic</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcoming.map((sched) => {
                  const scheduledDate = new Date(sched.scheduled_date);
                  const daysUntil = Math.ceil((scheduledDate - new Date()) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={sched.schedule_id} className="flex items-center justify-between p-4 rounded-lg bg-secondary/30 border border-border/50" data-testid={`schedule-item-${sched.schedule_id}`}>
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FileText className="h-6 w-6 text-primary" strokeWidth={1.5} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{sched.assessment_type === 'full' ? 'Full Diagnostic' : 'Express Scan'}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {scheduledDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                            {' at '}
                            {scheduledDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {sched.notes && <p className="text-xs text-muted-foreground/70 mt-1">{sched.notes}</p>}
                          <div className="flex items-center gap-2 mt-1">
                            {sched.reminder_24h ? (
                              <span className="flex items-center gap-1 text-[10px] text-primary">
                                <Bell className="h-3 w-3" /> Reminder on
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                                <BellOff className="h-3 w-3" /> No reminder
                              </span>
                            )}
                            {sched.reminder_sent && (
                              <span className="flex items-center gap-1 text-[10px] text-emerald-500">
                                <CheckCircle2 className="h-3 w-3" /> Sent
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSendReminder(sched.schedule_id)}
                          disabled={sendingReminder === sched.schedule_id}
                          data-testid={`send-reminder-${sched.schedule_id}`}
                          title="Send reminder now"
                        >
                          {sendingReminder === sched.schedule_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4" />
                          )}
                        </Button>
                        {daysUntil <= 0 ? (
                          <Link to={`/assessment/${sched.assessment_type}`}>
                            <Button size="sm" className="glow-primary" data-testid={`start-scheduled-${sched.schedule_id}`}>
                              Start Now
                              <ArrowRight className="ml-1 h-3 w-3" />
                            </Button>
                          </Link>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(sched.schedule_id)} data-testid={`cancel-schedule-${sched.schedule_id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Past / Completed */}
      {past.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-muted-foreground">
                <CheckCircle2 className="h-5 w-5" strokeWidth={1.5} />
                Past
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {past.map((sched) => (
                  <div key={sched.schedule_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/20 border border-border/30 opacity-70">
                    <div>
                      <span className="text-sm font-medium">{sched.assessment_type === 'full' ? 'Full Diagnostic' : 'Express Scan'}</span>
                      <p className="text-xs text-muted-foreground">{formatDate(sched.scheduled_date)}</p>
                    </div>
                    <Badge variant={sched.status === 'completed' ? 'default' : 'secondary'} className="text-[10px]">
                      {sched.status}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="bg-card border-border/50">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold">Ready to assess now?</h3>
                <p className="text-sm text-muted-foreground">Skip scheduling and start a diagnostic right away</p>
              </div>
              <div className="flex gap-3">
                <Link to="/assessment/express">
                  <Button variant="outline" data-testid="quick-express-btn">Express Scan</Button>
                </Link>
                <Link to="/assessment/full">
                  <Button className="glow-primary" data-testid="quick-full-btn">
                    Full Diagnostic
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
